// Predecessor links in the Schedule editor (pages/Scheduler).
//
// A link is stored as the KEY of the row it points at - never as that
// row's number. The number in the Preds box is only where the row happens
// to sit right now: inserting a row, moving one up or down, or deleting
// one renumbers every row after it, and links held as numbers then point
// at the wrong activity. Nothing looks broken when that happens - the
// dates still compute, they are just wrong - which is why the storage,
// and not the display, is the thing that has to be stable.
//
// A link is { key, type, lagDays }:
//
//   FS  predecessor finishes -> this starts   (the default)
//   SS  predecessor starts   -> this starts
//   FF  predecessor finishes -> this finishes
//   SF  predecessor starts   -> this finishes
//
// `lagDays` is in WORKING days and may be negative (a lead). The column
// accepts "12", "12FS+3d", "12SS-2" and reads back the short form: a
// plain FS link with no lag is still just "12", so the common case looks
// exactly as it always did.
//
// The scheduling engine (helpers/schedule/autoSchedule) works in 1-based
// row numbers, so `toEngineRows` translates once, at that single
// boundary, and carries the keys through as `predKeys` for the chart and
// the save path.

export const LINK_TYPES = ["FS", "SS", "FF", "SF"];

// A link in any shape this code has ever stored: a bare key (before
// types existed) or the full object.
export function linkOf(p) {
  if (p === null || p === undefined) return null;
  if (typeof p === "string") return { key: p, type: "FS", lagDays: 0 };
  if (!p.key) return null;
  const type = LINK_TYPES.indexOf(String(p.type || "").toUpperCase()) >= 0
    ? String(p.type).toUpperCase() : "FS";
  const lagDays = Number.isFinite(Number(p.lagDays)) ? Math.round(Number(p.lagDays)) : 0;
  return { key: p.key, type, lagDays };
}

const linksOf = (preds) => (preds || []).map(linkOf).filter(Boolean);

// "12FS+3d" / "12" / "12ss-2" -> { num, type, lagDays }, or null.
export function parseRelation(text) {
  const m = String(text == null ? "" : text)
    .trim()
    .match(/^(\d+)\s*(FS|SS|FF|SF)?\s*([+-]\s*\d+)?\s*d?a?y?s?$/i);
  if (!m) return null;
  const num = parseInt(m[1], 10);
  if (!Number.isFinite(num) || num < 1) return null;
  return {
    num,
    type: m[2] ? m[2].toUpperCase() : "FS",
    lagDays: m[3] ? parseInt(m[3].replace(/\s+/g, ""), 10) : 0,
  };
}

// The short form: 12, 12SS, 12FS+3d, 12SS-2d.
export function formatRelation(num, type, lagDays) {
  const t = LINK_TYPES.indexOf(String(type || "").toUpperCase()) >= 0
    ? String(type).toUpperCase() : "FS";
  const lag = Number(lagDays) || 0;
  if (t === "FS" && !lag) return String(num);
  return `${num}${t}${lag ? `${lag > 0 ? "+" : "-"}${Math.abs(lag)}d` : ""}`;
}

// Readers of a SAVED activity's `dependencies` (the chart, the print
// pages): one accessor each, so no caller has to know whether the row
// came back in the old shape or the new one.
export const depId = (d) =>
  String(d && d.predecessorId !== undefined && d.predecessorId !== null
    ? d.predecessorId
    : (d && d._id !== undefined ? d._id : d));
export const depType = (d) => {
  const t = String((d && d.type) || "").toUpperCase();
  return LINK_TYPES.indexOf(t) >= 0 ? t : "FS";
};
export const depLag = (d) => (Number.isFinite(Number(d && d.lagDays)) ? Math.round(Number(d.lagDays)) : 0);

// Display: the rows this one follows, as the numbers they sit at now.
// Links to rows that no longer exist simply do not show.
export function predsToText(preds, idxByKey) {
  return linksOf(preds)
    .map((l) => ({ ...l, i: idxByKey.get(l.key) }))
    .filter((l) => l.i !== undefined)
    .sort((a, b) => a.i - b.i)
    .map((l) => formatRelation(l.i + 1, l.type, l.lagDays))
    .join(",");
}

// Entry: "2,3SS-1" -> links to rows 2 and 3. A number outside the
// schedule, the row's own number and a repeat of the same (row, type)
// are not links.
export function textToPreds(text, rows, selfKey) {
  const out = [];
  const seen = new Set();
  for (const part of String(text == null ? "" : text).split(",")) {
    const rel = parseRelation(part);
    if (!rel || rel.num > rows.length) continue;
    const key = rows[rel.num - 1].key;
    if (key === selfKey) continue;
    const sig = `${key}:${rel.type}`;
    if (seen.has(sig)) continue;
    seen.add(sig);
    out.push({ key, type: rel.type, lagDays: rel.lagDays });
  }
  return out;
}

// Engine boundary: keys -> 1-based row numbers, keys kept as `predKeys`.
// `isZeroDuration` marks milestones / sign-offs / area releases for the
// engine, which calls them all `milestone`.
export function toEngineRows(rows, isZeroDuration) {
  const numByKey = new Map((rows || []).map((r, i) => [r.key, i + 1]));
  return (rows || []).map((r) => {
    const links = linksOf(r.predecessors);
    return {
      ...r,
      predKeys: links,
      predecessors: links
        .map((l) => ({ num: numByKey.get(l.key), type: l.type, lagDays: l.lagDays }))
        .filter((l) => l.num),
      milestone: !!(isZeroDuration && isZeroDuration(r)),
    };
  });
}

// The save path: links as the API stores them (utils/dependency.js).
// `idByKey` maps an editor row key to the saved activity's _id.
export function toApiDependencies(preds, idByKey) {
  return linksOf(preds)
    .map((l) => ({ predecessorId: idByKey.get(l.key), type: l.type, lagDays: l.lagDays }))
    .filter((d) => d.predecessorId);
}

// The other direction: what the API gives back -> editor links.
// `keyById` maps a saved activity's _id to its editor row key.
export function fromApiDependencies(dependencies, keyById) {
  return (dependencies || [])
    .map((d) => {
      const id = d && d.predecessorId !== undefined ? d.predecessorId : d;
      const key = keyById.get(String(id));
      return key ? { key, type: (d && d.type) || "FS", lagDays: (d && d.lagDays) || 0 } : null;
    })
    .filter(Boolean);
}

// Deleting a row also drops the links that pointed at it, so the editor
// state never carries a link to something that is gone.
export function removeRow(rows, key) {
  return (rows || [])
    .filter((r) => r.key !== key)
    .map((r) => {
      const links = linksOf(r.predecessors);
      return links.some((l) => l.key === key)
        ? { ...r, predecessors: links.filter((l) => l.key !== key) }
        : r;
    });
}
